import toast from "@/lib/toast";
import { v4 as uuid } from "uuid";
import { GetVideoResponseType } from "@/features/storage/sheets/hooks/useGetVideo";
import { Dispatch, SetStateAction } from "react";
import { SrclangType, VideoInteraction } from "@yedalabs/schema";
import { t } from "@/lib/localization";
import { defaultContentForType } from "../lib/utils";

export const useInteractionsList = ({
    video,
    interactions,
    srclang,
    setInteractions,
    setSelectedInteraction,
    setChanges,
    setInteractionModalOpen,
}: {
    video: GetVideoResponseType["data"];
    interactions: VideoInteraction[];
    srclang?: SrclangType;
    setInteractions: Dispatch<SetStateAction<VideoInteraction[]>>;
    setSelectedInteraction: Dispatch<SetStateAction<VideoInteraction | null>>;
    setChanges: Dispatch<SetStateAction<boolean>>;
    setInteractionModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
    const createInteraction = () => {
        if (!srclang) {
            toast.error(t("toast.error.default"), {
                keyName: "toast.error.default",
            });
            return;
        }

        setChanges(true);

        const newInteraction = {
            id: uuid(),
            videoId: video.id,
            type: "open",
            srclang,
            timestamp: getAvailableTimestampAtTheEnd(),
            content: defaultContentForType("open"),
            createdAt: null,
            updatedAt: null,
        } as VideoInteraction;

        setInteractions((prev) =>
            [...prev, newInteraction].sort((a, b) => a.timestamp - b.timestamp)
        );

        setSelectedInteraction(newInteraction);
        setInteractionModalOpen(true);
    };

    const editInteraction = (id: string) => {
        const interaction = interactions.find((i) => i.id === id);
        if (!interaction) {
            toast.error(t("toast.error.interaction-not-found"), {
                keyName: "toast.error.interaction-not-found",
            });
            return;
        }

        setSelectedInteraction(interaction);
        setInteractionModalOpen(true);
    };

    const deleteInteraction = (id: string) => {
        setInteractions((prev) => prev.filter((i) => i.id !== id));
        setChanges(true);
    };

    const duplicateInteraction = (id: string) => {
        const interactionToDuplicate = interactions.find((i) => i.id === id);
        if (!interactionToDuplicate) {
            toast.error(t("toast.error.interaction-not-found"), {
                keyName: "toast.error.interaction-not-found",
            });
            return;
        }

        const newInteraction: VideoInteraction = {
            ...interactionToDuplicate,
            id: uuid(),
            timestamp: getAvailableTimestampAtTheEnd(),
        };

        setInteractions((prev) =>
            [...prev, newInteraction].sort((a, b) => a.timestamp - b.timestamp)
        );
        setChanges(true);
    };

    const getInteractionTitle = (id: string) => {
        const interaction = interactions.find((i) => i.id === id);
        if (!interaction) {
            console.log("interaction not found in getInteractionTitle");
            return "";
        }

        return interaction.content.question;
    };

    const getAvailableTimestampAtTheEnd = () => {
        const sortedInteractions = interactions.sort(
            (a, b) => a.timestamp - b.timestamp
        );
        const lastInteractionTimestamp = sortedInteractions.length
            ? sortedInteractions[sortedInteractions.length - 1].timestamp
            : 0;

        return video.length
            ? Math.round((video.length - lastInteractionTimestamp) / 2)
            : 0;
    };

    return {
        createInteraction,
        editInteraction,
        deleteInteraction,
        duplicateInteraction,
        getInteractionTitle,
    };
};
